import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Fuel, Trash2 } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { businessDateStr } from "@/lib/businessDate";
import { useAuth } from "@/contexts/AuthContext";

const statusColors = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  conferido: "bg-green-100 text-green-800 border-green-200",
  rejeitado: "bg-red-100 text-red-800 border-red-200",
  correcao: "bg-orange-100 text-orange-800 border-orange-200",
};

const today = businessDateStr();

export default function Closings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [status, setStatus] = useState("all");
  const [abasteceu, setAbasteceu] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (status !== "all") params.status = status;
      if (abasteceu === "yes") params.abasteceu = "true";
      else if (abasteceu === "no") params.abasteceu = "false";
      const { data } = await api.get("/closings", { params });
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [dateFrom, dateTo, status, abasteceu]);

  async function removeClosing(closing) {
    if (!window.confirm(`Apagar definitivamente o fechamento de ${closing.driver_nome} em ${closing.date}?`)) return;
    try {
      await api.delete(`/closings/${closing.id}`);
      toast.success("Fechamento apagado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const filtered = rows.filter((r) => {
    const t = q.toLowerCase();
    return !t || r.driver_nome.toLowerCase().includes(t) || (r.rota || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-700">Operação</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Fechamentos</h1>
        <p className="mt-1 text-sm text-slate-500">{filtered.length} resultados</p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-5">
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">De</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Até</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="conferido">Conferido</SelectItem>
              <SelectItem value="rejeitado">Rejeitado</SelectItem>
              <SelectItem value="correcao">Correção</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Abasteceu</Label>
          <Select value={abasteceu} onValueChange={setAbasteceu}>
            <SelectTrigger data-testid="filter-abasteceu"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="yes">Motoristas que abasteceram</SelectItem>
              <SelectItem value="no">Não abasteceram</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Buscar</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Motorista ou rota" className="pl-9" data-testid="filter-search" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Motorista</TableHead>
                <TableHead>Rota</TableHead>
                <TableHead className="text-right">Entregues</TableHead>
                <TableHead className="text-right">Retornos</TableHead>
                <TableHead className="text-right">Fora</TableHead>
                <TableHead className="text-right">Sucesso</TableHead>
                <TableHead>Abast.</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={10} className="py-10 text-center text-slate-400">Carregando…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={10} className="py-10 text-center text-slate-400">Sem resultados.</TableCell></TableRow>
              )}
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`closing-row-${c.id}`}>
                  <TableCell className="text-slate-600">{c.date}</TableCell>
                  <TableCell className="font-medium text-slate-900">{c.driver_nome}</TableCell>
                  <TableCell>{c.rota || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{c.entregues}</TableCell>
                  <TableCell className="text-right font-mono">{c.retornos}</TableCell>
                  <TableCell className="text-right font-mono">{c.fora_lista_qtd}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-blue-700">{c.sucesso}%</TableCell>
                  <TableCell>
                    {c.abastecimento_used ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800" data-testid={`fuel-badge-${c.id}`}>
                        <Fuel className="h-3 w-3" />
                        R$ {Number(c.abastecimento_valor || 0).toFixed(2).replace(".", ",")}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell><Badge className={`border ${statusColors[c.status] || ""}`}>{c.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <Link
                        to={`/admin/fechamentos/${c.id}`}
                        className="rounded-md bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
                        data-testid={`closing-open-${c.id}`}
                      >
                        Abrir
                      </Link>
                      {user?.role === "admin" && (
                        <button
                          type="button"
                          onClick={() => removeClosing(c)}
                          className="rounded-md border border-red-200 bg-white p-1.5 text-red-600 hover:bg-red-50"
                          title="Apagar fechamento"
                          data-testid={`closing-delete-${c.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
